import { useState, useEffect, useRef } from "react"
import { Eye, FileText, Download } from "lucide-react"
import { Toast } from "primereact/toast"
import { ClimbingBoxLoader } from "react-spinners"
import API_BASE_URL from "../../Config"

const formatPeriodToMMYYYY = (period) => {
  if (!period) return "-"
  try {
    const date = new Date(period)
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    const month = monthNames[date.getMonth()]
    const year = date.getFullYear()
    return `${month}/${year}`
  } catch {
    return period
  }
}

const readMessage = async (response) => {
  try {
    const data = await response.clone().json()
    return data?.message || data?.detail || data?.error || JSON.stringify(data)
  } catch {
    try {
      return await response.text()
    } catch {
      return "Unknown error"
    }
  }
}

const Processed = () => {
  const [processedData, setProcessedData] = useState([])
  const [loading, setLoading] = useState(false)
  const [generatingIds, setGeneratingIds] = useState(new Set())
  const [downloadingIds, setDownloadingIds] = useState(new Set())
  const toast = useRef(null)
  const isBusy = loading || generatingIds.size > 0 || downloadingIds.size > 0

  useEffect(() => {
    fetchProcessedData()
  }, [])

  const fetchProcessedData = async () => {
    setLoading(true)
    try {
      const token = sessionStorage.getItem("token")
      const clientId = sessionStorage.getItem("client_id")

      const response = await fetch(`${API_BASE_URL}api/processFilter/?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        const data = await response.json()
        let records = []
        if (Array.isArray(data?.records)) records = data.records
        else if (Array.isArray(data?.data)) records = data.data
        else if (Array.isArray(data)) records = data
        else if (Array.isArray(data?.results)) records = data.results
        setProcessedData(records)
      } else {
        const errorText = await readMessage(response)
        if (response.status === 500 || response.status === 504) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Something went wrong!!!",
            life: 0,
            sticky: true,
          })
        } else if (response.status === 207) {
          toast.current?.show({ severity: "warn", summary: "Warning", detail: errorText, life: 0, sticky: true })
        } else {
          toast.current?.show({ severity: "error", summary: "Error", detail: errorText, life: 0, sticky: true })
        }
      }
    } catch (error) {
      toast.current?.show({ severity: "error", summary: "Network Error", detail: error.message, life: 0, sticky: true })
    } finally {
      setLoading(false)
    }
  }

  const handleViewClick = () => {
    window.location.href = "/repository"
  }

  const handleGenerateClick = async (record, index) => {
    const baseId = record.id ?? `record-${index}`
    const uniqueId = `${baseId}-main`
    if (generatingIds.has(uniqueId)) return
    setGeneratingIds((prev) => new Set([...prev, uniqueId]))
    try {
      const token = sessionStorage.getItem("token")
      const clientId = sessionStorage.getItem("client_id")

      const response = await fetch(`${API_BASE_URL}api/generate_registers/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ client_id: clientId, period: record.period }),
      })

      if (response.ok) {
        const result = await response.json()
        if (response.status === 207) {
          toast.current?.show({
            severity: "warn",
            summary: "Warning",
            detail: result.message || "Report generated with warnings",
            life: 0,
            sticky: true,
          })
        } else {
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: result.message || "Report generated successfully",
            life: 0,
            sticky: true,
          })
        }
        fetchProcessedData()
      } else {
        const errorText = await readMessage(response)
        if (response.status === 500 || response.status === 504) {
          toast.current?.show({
            severity: "error",
            summary: "Generation Error",
            detail: "SOMETHING WENT WRONG!!!",
            life: 0,
            sticky: true,
          })
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Generation Error",
            detail: errorText,
            life: 0,
            sticky: true,
          })
        }
      }
    } catch (error) {
      toast.current?.show({ severity: "error", summary: "Network Error", detail: error.message, life: 0, sticky: true })
    } finally {
      setGeneratingIds((prev) => {
        const next = new Set(prev)
        next.delete(uniqueId)
        return next
      })
    }
  }

  const handleDownloadPreAudit = async (record, index) => {
    const baseId = record.id ?? `record-${index}`
    const uniqueId = `${baseId}-download`
    if (downloadingIds.has(uniqueId)) return
    setDownloadingIds((prev) => new Set([...prev, uniqueId]))
    try {
      const token = sessionStorage.getItem("token")
      const clientId = sessionStorage.getItem("client_id")

      const response = await fetch(
        `${API_BASE_URL}api/download_preAuditReport/?client_id=${clientId}&period=${record.period}`,
        { method: "GET", headers: { Authorization: `Bearer ${token}` } },
      )

      if (response.ok) {
        const blob = await response.blob()
        let filename = `PreAudit_Report_${formatPeriodToMMYYYY(record.period)}.pdf`
        const disposition = response.headers.get("Content-Disposition")
        if (disposition?.includes("filename=")) {
          const match = disposition.match(/filename="?([^"]+)"?/)
          if (match?.[1]) filename = match[1]
        }
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)

        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Pre-audit report downloaded successfully",
          life: 3000,
        })
      } else {
        const errorText = await readMessage(response)
        if (response.status === 500) {
          toast.current?.show({
            severity: "error",
            summary: "Download Error",
            detail: "SOMETHING WENT WRONG!!!",
            life: 0,
            sticky: true,
          })
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Download Error",
            detail: errorText,
            life: 0,
            sticky: true,
          })
        }
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Network Error",
        detail: error.message,
        life: 0,
        sticky: true,
      })
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev)
        next.delete(uniqueId)
        return next
      })
    }
  }

  const handleGeneratePreAudit = async (record, index) => {
    const baseId = record.id ?? `record-${index}`
    const uniqueId = `${baseId}-preaudit`
    if (generatingIds.has(uniqueId)) return
    setGeneratingIds((prev) => new Set([...prev, uniqueId]))
    try {
      const token = sessionStorage.getItem("token")
      const clientId = sessionStorage.getItem("client_id")

      const response = await fetch(`${API_BASE_URL}api/pre_audit_report/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ client_id: clientId, period: record.period }),
      })

      if (response.ok) {
        const result = await response.json()
        if (response.status === 207) {
          toast.current?.show({
            severity: "warn",
            summary: "Warning",
            detail: result.message || "Pre-audit report generated with warnings",
            life: 0,
            sticky: true,
          })
        } else {
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: result.message || "Pre-audit report generated successfully",
            life: 0,
            sticky: true,
          })
        }
        fetchProcessedData()
      } else {
        const errorText = await readMessage(response)
        if (response.status === 500) {
          toast.current?.show({
            severity: "error",
            summary: "Generation Error",
            detail: "SOMETHING WENT WRONG!!!",
            life: 0,
            sticky: true,
          })
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Generation Error",
            detail: errorText,
            life: 0,
            sticky: true,
          })
        }
      }
    } catch (error) {
      toast.current?.show({ severity: "error", summary: "Network Error", detail: error.message, life: 0, sticky: true })
    } finally {
      setGeneratingIds((prev) => {
        const next = new Set(prev)
        next.delete(uniqueId)
        return next
      })
    }
  }

  const renderPreAuditButtons = (record, index) => {
    const preAuditStatus = record.preaudit_status || record.preauditStatus || ""
    const baseId = record.id ?? `record-${index}`
    const uniqueId = `${baseId}-preaudit`
    const isGenerating = generatingIds.has(uniqueId)
    const isDownloading = downloadingIds.has(`${baseId}-download`)
    const isPreAuditProcessed = preAuditStatus.toLowerCase() === "processed"

    return (
      <div className="flex gap-2 items-center justify-center">
        {/* Download Button */}
        <button
          onClick={() => handleDownloadPreAudit(record, index)}
          disabled={!isPreAuditProcessed || isDownloading}
          aria-disabled={!isPreAuditProcessed || isDownloading}
          aria-busy={isDownloading}
          className={`px-3 py-2 rounded-lg flex items-center gap-2 text-xs font-medium transition-colors ${
            !isPreAuditProcessed || isDownloading
              ? "bg-gray-500/20 text-gray-400 cursor-not-allowed opacity-50"
              : "bg-purple-500/20 text-purple-400 hover:bg-purple-500/30"
          }`}
          title={isPreAuditProcessed ? "Download Pre-Audit" : "Pre-Audit not generated yet"}
        >
          <Download size={14} />
          {isDownloading ? "Downloading..." : "Download"}
        </button>

        {/* Generate Button */}
        <button
          onClick={() => handleGeneratePreAudit(record, index)}
          disabled={isGenerating || isPreAuditProcessed}
          aria-disabled={isGenerating || isPreAuditProcessed}
          aria-busy={isGenerating}
          className={`px-3 py-2 rounded-lg flex items-center gap-2 text-xs font-medium transition-colors ${
            isPreAuditProcessed
              ? "bg-gray-500/20 text-gray-400 cursor-not-allowed opacity-50"
              : isGenerating
                ? "bg-orange-500/20 text-orange-400 opacity-50 cursor-not-allowed"
                : "bg-orange-500/20 text-orange-400 hover:bg-orange-500/30"
          }`}
          title={isPreAuditProcessed ? "Pre-Audit Already Processed" : "Generate Pre-Audit"}
        >
          <FileText size={14} />
          {isGenerating ? "Generating..." : "Generate"}
        </button>
      </div>
    )
  }

  const renderActionButton = (record, index) => {
    const status = record.status || record.Status || ""
    const baseId = record.id ?? `record-${index}`
    const uniqueId = `${baseId}-main`
    const isGenerating = generatingIds.has(uniqueId)
    const isProcessed = status.toLowerCase() === "processed"

    return (
      <div className="flex gap-2 items-center justify-center">
        <button
          onClick={() => handleViewClick(record)}
          className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
          title="View Details"
        >
          <Eye size={16} />
        </button>

        <button
          onClick={() => handleGenerateClick(record, index)}
          disabled={isGenerating || isProcessed}
          aria-disabled={isGenerating || isProcessed}
          aria-busy={isGenerating}
          className={`px-3 py-2 rounded-lg flex items-center gap-2 text-xs font-medium transition-colors ${
            isProcessed
              ? "bg-gray-500/20 text-gray-400 cursor-not-allowed opacity-50"
              : isGenerating
                ? "bg-green-500/20 text-green-400 opacity-50 cursor-not-allowed"
                : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
          }`}
          title={isProcessed ? "Already Processed" : "Generate Report"}
        >
          <FileText size={14} />
          {isGenerating ? "Generating..." : "Generate"}
        </button>
      </div>
    )
  }

  const preAuditCompanies = ["Duster", "Tickbox"] // Add your second company name here
  const hasAnyPreAudit = processedData.some((r) => {
    const companyName = r.companyName || r.company_name || r.CompanyName || ""
    return preAuditCompanies.some((company) => companyName.includes(company))
  })

  return (
    <div className="relative min-h-screen">
      {isBusy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <ClimbingBoxLoader color="#d97925" size={15} />
        </div>
      )}

      <div className="min-h-screen bg-cover bg-center p-3 sm:p-6" style={{ backgroundImage: "url('/background.jpg')" }}>
        <Toast ref={toast} position="top-right" />

        <div className="max-w-7xl mx-auto">
          <div className="backdrop-blur-xl bg-gradient-to-br from-white/15 to-white/5 border border-white/30 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-3 sm:p-6 border-b border-white/20">
              <h2 className="text-lg sm:text-2xl font-bold text-white">
                Processed Records ({processedData.length} records)
              </h2>
            </div>

            {/* Mobile */}
            <div className="block sm:hidden">
              {processedData.length > 0 ? (
                <div className="p-3 space-y-3">
                  {processedData.map((row, index) => {
                    const companyName = row.companyName || row.company_name || row.CompanyName || ""
                    const showPreAudit = preAuditCompanies.some((company) => companyName.includes(company))
                    return (
                      <div
                        key={row.id || index}
                        className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20"
                      >
                        <div className="space-y-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="text-white/70 text-xs font-medium">S.No:</span>
                              <div className="text-white font-bold">{index + 1}</div>
                            </div>
                            <div>
                              <span className="text-white/70 text-xs font-medium">Month:</span>
                              <div className="text-white font-bold">{formatPeriodToMMYYYY(row.period)}</div>
                            </div>
                          </div>

                          <div>
                            <span className="text-white/70 text-xs font-medium">Company Name:</span>
                            <div className="text-white font-bold">{companyName}</div>
                          </div>

                          {showPreAudit && (
                            <div>
                              <span className="text-white/70 text-xs font-medium">Pre-Audit:</span>
                              <div className="mt-1">{renderPreAuditButtons(row, index)}</div>
                            </div>
                          )}

                          <div className="flex justify-end">
                            <div className="flex gap-2">{renderActionButton(row, index)}</div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="p-6 text-center text-white/70">
                  {loading ? "Loading data..." : "No processed records available."}
                </div>
              )}
            </div>

            {/* Desktop */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20 bg-white/5">
                    <th className="px-4 lg:px-6 py-4 text-left text-sm font-bold text-white">S.No</th>
                    <th className="px-4 lg:px-6 py-4 text-left text-sm font-bold text-white">MMM/YYYY</th>
                    <th className="px-4 lg:px-6 py-4 text-left text-sm font-bold text-white">Company Name</th>
                    {hasAnyPreAudit && (
                      <th className="px-4 lg:px-6 py-4 text-center text-sm font-bold text-white">Pre-Audit</th>
                    )}
                    <th className="px-4 lg:px-6 py-4 text-center text-sm font-bold text-white">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {processedData.length > 0 ? (
                    processedData.map((row, index) => {
                      const companyName = row.companyName || row.company_name || row.CompanyName || ""
                      const showPreAudit = preAuditCompanies.some((company) => companyName.includes(company))
                      return (
                        <tr
                          key={row.id || index}
                          className="border-b border-white/10 hover:bg-white/10 transition-colors duration-200"
                        >
                          <td className="px-4 lg:px-6 py-4 text-sm text-white font-medium">{index + 1}</td>
                          <td className="px-4 lg:px-6 py-4 text-sm text-white font-medium">
                            {formatPeriodToMMYYYY(row.period)}
                          </td>
                          <td className="px-4 lg:px-6 py-4 text-sm text-white">{companyName}</td>

                          {hasAnyPreAudit && (
                            <td className="px-4 lg:px-6 py-4 text-sm">
                              {showPreAudit ? renderPreAuditButtons(row, index) : null}
                            </td>
                          )}

                          <td className="px-4 lg:px-6 py-4 text-sm">
                            <div className="flex justify-center gap-2">{renderActionButton(row, index)}</div>
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={4 + (hasAnyPreAudit ? 1 : 0)}
                        className="px-4 lg:px-6 py-12 text-center text-white/70 text-lg"
                      >
                        {loading ? "Loading data..." : "No processed records available."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Processed
